// TopNav — the app's global header bar.
//
// Replaces the left sidebar (Aug 2026 redesign): brand on the left, the
// admin-configured navigation as dropdown menus beside it, the Zebra Search
// bar centred, and the user account menu on the right. Mounted ONCE in
// App.jsx's ProtectedLayout (under the TabBar, above the tab content) —
// individual pages no longer carry nav chrome.
//
// Data: the same server-driven config the sidebar consumed —
//   GET /api/sidebar     -> ordered item list (builtin + custom entries,
//                           optional restrictedGroups per item)
//   GET /api/navigation  -> the Navigation menu's tree (nested children)
// Semantics preserved from the sidebar/NavigationFlyout implementation:
// group filtering, internal links open as app tabs (middle-click =
// background tab), web addresses open a new browser tab. What counts as
// internal is decided by utils/navUrls.js (this origin only — an address
// on any other host, Zebra's or not, is a web address and opens outside).
//
// IMPORTANT: no `transform` may ever be applied to this header or its
// ancestors — ZebraSearchBar's dropdown is position:fixed and NOT portalled,
// so a transformed ancestor would become its containing block and break its
// viewport anchoring. Dropdown panels animate themselves (children), which
// is safe.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { useTheme } from '@platform/app-shell/frontend/contexts/ThemeContext';
import { isExternalNavUrl } from '@platform/app-shell/frontend/utils/navUrls';
import { visibleNavCount, entriesAsMenuRows } from '@platform/app-shell/frontend/utils/navOverflow';
import NavItemIcon from '@platform/app-shell/frontend/components/TopNav/NavItemIcon';
import useNavEntries, { resolveUrl } from '@platform/app-shell/frontend/components/TopNav/useNavEntries';
import ZebraSearchBar from '@modules/zebra-search/frontend/components/ZebraSearchBar';
import UserAccountMenu from '@platform/app-shell/frontend/components/UserAccountMenu';
import NotificationBell from '@modules/notifications/frontend/components/NotificationBell/NotificationBell';
import zebraLogoOnDark from '@app/assets/zebra-stride-logo.png';
import zebraLogoOnLight from '@app/assets/zebra-stride-logo-light.png';
import { BRAND_NAME } from '@client/config/client.config';
import { useLookAndFeel, hexToRgbTriplet } from '@platform/app-shell/frontend/lookAndFeel';
import NavDrawer from '@platform/app-shell/frontend/components/TopNav/NavDrawer';
import WorkspaceBrand from '@platform/app-shell/frontend/components/TopNav/WorkspaceBrand';
import '@platform/app-shell/frontend/components/TopNav/TopNav.css';

// Open a resolved address outside the shell (a web address) or as an app
// tab (a Stride page). `open` is openTab / openBackgroundTab.
function openResolved(url, label, open) {
  if (isExternalNavUrl(url)) window.open(url, '_blank', 'noopener,noreferrer');
  else open(url, label);
}

// Small "leaves the app" mark on a row that opens a web address.
const ExternalMark = () => (
  <svg className="tn-ext-mark" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M18 13v6H5V6h6" />
  </svg>
);

// One dropdown row; parents open a nested submenu flying out to the side.
// Submenus are position:fixed at viewport coordinates (computed from the
// anchor row) so they ESCAPE the dropdown panel — an absolutely-positioned
// child inside a scrollable panel would be clipped by it and force
// horizontal scrollbars (the original bug). Fixed coords are safe here:
// nothing above the header applies a transform.
const SUBMENU_WIDTH = 236;
function MenuTree({ items, depth, onNavigate }) {
  const { openBackgroundTab } = useTab();
  const [openIndex, setOpenIndex] = useState(null);
  const [submenuPos, setSubmenuPos] = useState(null);
  const leaveTimer = useRef(null);
  const rowRefs = useRef({});
  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  const openSubmenu = (index) => {
    const el = rowRefs.current[index];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Flip to the left edge when the right side would leave the viewport;
    // clamp the top so a tall submenu stays fully on screen.
    const left = rect.right + SUBMENU_WIDTH > window.innerWidth - 8
      ? Math.max(8, rect.left - SUBMENU_WIDTH + 2)
      : rect.right - 2;
    const top = Math.max(8, Math.min(rect.top - 7, window.innerHeight - 340));
    setSubmenuPos({ left, top });
    setOpenIndex(index);
  };

  const enter = (index, item) => {
    clearTimeout(leaveTimer.current);
    if (item.children && item.children.length > 0) openSubmenu(index);
    else { setOpenIndex(null); setSubmenuPos(null); }
  };
  const leave = () => {
    leaveTimer.current = setTimeout(() => { setOpenIndex(null); setSubmenuPos(null); }, 200);
  };

  const activate = (item) => {
    if (item.children && item.children.length > 0) return; // parents don't navigate
    const url = resolveUrl(item.url);
    if (!url) return;
    onNavigate(url, item.tabLabel || item.label);
  };

  const auxActivate = (e, item) => {
    if (e.button !== 1) return;
    const url = resolveUrl(item.url);
    if (!url) return;
    e.preventDefault();
    openResolved(url, item.tabLabel || item.label, openBackgroundTab);
  };

  return (
    <div className="tn-menu-list" role="menu">
      {items.map((item, index) => {
        const hasChildren = item.children && item.children.length > 0;
        return (
          <div
            key={item.id || `${depth}-${index}`}
            className="tn-menu-item-wrap"
            onMouseEnter={() => enter(index, item)}
            onMouseLeave={leave}
          >
            <button
              type="button"
              role="menuitem"
              ref={(el) => { rowRefs.current[index] = el; }}
              className={`tn-menu-item ${hasChildren && openIndex === index ? 'tn-menu-item--open' : ''}`}
              aria-haspopup={hasChildren ? 'menu' : undefined}
              aria-expanded={hasChildren ? openIndex === index : undefined}
              onClick={() => (hasChildren
                ? (openIndex === index ? (setOpenIndex(null), setSubmenuPos(null)) : openSubmenu(index))
                : activate(item))}
              onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
              onAuxClick={(e) => auxActivate(e, item)}
            >
              <NavItemIcon item={item} fallback={item.iconFallback || null} />
              <span className="tn-menu-item-label">{item.label}</span>
              {!hasChildren && isExternalNavUrl(resolveUrl(item.url)) && <ExternalMark />}
              {hasChildren && <span className="tn-menu-item-arrow" aria-hidden="true">▸</span>}
            </button>
            {hasChildren && openIndex === index && submenuPos && (
              <div
                className="tn-submenu"
                role="presentation"
                style={{ left: submenuPos.left, top: submenuPos.top }}
                onMouseEnter={() => clearTimeout(leaveTimer.current)}
                onMouseLeave={leave}
              >
                <MenuTree items={item.children} depth={depth + 1} onNavigate={onNavigate} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A top-level menubar entry with a dropdown panel.
function TopNavMenu({ id, label, item, iconFallback, items, emptyText, isOpen, onToggle, onHoverSwitch, onNavigate, className = '' }) {
  return (
    <div className={`tn-menu ${className}`} onMouseEnter={onHoverSwitch}>
      <button
        type="button"
        className={`tn-trigger ${isOpen ? 'tn-trigger--open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={onToggle}
        title={label}
      >
        <NavItemIcon item={item} fallback={iconFallback} />
        <span className="tn-trigger-label">{label}</span>
        <svg className="tn-chevron" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
          <path d="M1 3.5 L5 7 L9 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isOpen && (
        <div className="tn-dropdown" role="presentation">
          {(!items || items.length === 0)
            ? <div className="tn-empty">{emptyText || 'Nothing here yet'}</div>
            : <MenuTree items={items} depth={0} onNavigate={onNavigate} />}
        </div>
      )}
    </div>
  );
}

// A top-level entry that navigates directly (no dropdown).
function TopNavLink({ label, item, iconFallback, onActivate, onAuxActivate, external }) {
  return (
    <button
      type="button"
      className="tn-trigger"
      onClick={onActivate}
      onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
      onAuxClick={onAuxActivate}
      title={label}
    >
      <NavItemIcon item={item} fallback={iconFallback} />
      <span className="tn-trigger-label">{label}</span>
      {external && <ExternalMark />}
    </button>
  );
}

export default function TopNav() {
  const { openTab, openBackgroundTab, openHome } = useTab();
  // What the bar shows, decided ONCE (utils/navEntries.js): each visible
  // item as a menu (a tree that opens) or a link (an address). The menubar
  // below, the phone drawer and the Home page all draw from this list, so
  // an item added to the navigation cannot appear in one and be missing
  // from another.
  const { entries } = useNavEntries();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const rootRef = useRef(null);
  const brandRef = useRef(null);
  const menusRef = useRef(null);
  const searchRef = useRef(null);
  const rightRef = useRef(null);
  const measureRef = useRef(null);

  // How many entries stay on the bar; the rest fold into More. The bar
  // measures every entry at its natural width in a hidden row (same markup,
  // same breakpoints — a label hidden at 900px is hidden there too), then
  // utils/navOverflow.js decides. The room is the header less the brand, the
  // right-hand cluster and the search pill's floor (.tn-search min-width),
  // so search, alerts and the account never get squeezed by the menus.
  const [visibleCount, setVisibleCount] = useState(entries.length);
  const recount = useCallback(() => {
    const header = rootRef.current;
    const measure = measureRef.current;
    const menus = menusRef.current;
    if (!header || !measure || !menus) return;
    // Phone width: the menubar is gone (the drawer holds the navigation).
    if (getComputedStyle(menus).display === 'none') return;
    const triggers = Array.from(measure.children);
    if (triggers.length === 0) { setVisibleCount(0); return; }
    const widths = triggers.map((el) => el.getBoundingClientRect().width);
    const moreWidth = widths.pop();
    const hs = getComputedStyle(header);
    const headerGap = parseFloat(hs.columnGap) || 0;
    const searchFloor = searchRef.current ? parseFloat(getComputedStyle(searchRef.current).minWidth) || 0 : 0;
    const available = header.clientWidth
      - (parseFloat(hs.paddingLeft) || 0) - (parseFloat(hs.paddingRight) || 0)
      - (brandRef.current ? brandRef.current.getBoundingClientRect().width : 0)
      - (rightRef.current ? rightRef.current.getBoundingClientRect().width : 0)
      - searchFloor
      - headerGap * 3; // brand | menus | search | right
    const gap = parseFloat(getComputedStyle(measure).columnGap) || 0;
    setVisibleCount(visibleNavCount(widths, Math.floor(available), moreWidth, gap));
  }, []);

  useLayoutEffect(() => { recount(); }, [entries, recount]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => recount());
    [rootRef, measureRef, rightRef, brandRef].forEach((r) => { if (r.current) ro.observe(r.current); });
    return () => ro.disconnect();
  }, [recount]);

  const shown = entries.slice(0, visibleCount);
  const folded = entries.slice(visibleCount);
  const MORE_ID = '__more';

  // Close on outside click / Escape.
  useEffect(() => {
    if (!openMenuId) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenMenuId(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenuId(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenuId]);

  const navigateTo = useCallback((url, label) => {
    setOpenMenuId(null);
    openResolved(url, label, openTab);
  }, [openTab]);

  const linkAuxActivate = useCallback((e, item) => {
    if (e.button !== 1) return;
    const url = resolveUrl(item.url);
    if (!url) return;
    e.preventDefault();
    openResolved(url, item.label, openBackgroundTab);
  }, [openBackgroundTab]);

  // Menubar behavior: once a menu is open, hovering a sibling opens it.
  const hoverSwitch = (id) => {
    if (openMenuId && openMenuId !== id) setOpenMenuId(id);
  };

  // Site look-and-feel (admin-set, Settings → Preferences): logo/title/nav
  // colours override the defaults; absent values fall through to the
  // config-seam brand, the bundled logos and the CSS tokens.
  //
  // The nav colours are the LIGHT theme's, as in Jira: the bar's ink is the
  // theme's text token, so a colour picked against the light theme painted
  // under the dark theme's pale ink is unreadable (a light bar on a dark
  // app, with near-white text). In dark mode the bar keeps its own tokens.
  const lookAndFeel = useLookAndFeel();
  const { isDark } = useTheme();
  const navStyle = {};
  //
  // The SITE COLOUR (lookAndFeel.accent) is the admin's one knob: it paints
  // --accent everywhere and the selected nav item here, so the bar agrees
  // with the buttons under it. navHighlight is what sites set before the
  // site colour existed and is still honoured when no accent is chosen.
  const highlight = lookAndFeel.accent || lookAndFeel.navHighlight;
  if (!isDark && lookAndFeel.navBackground) navStyle['--tn-bg'] = lookAndFeel.navBackground;
  if (!isDark && highlight) {
    navStyle['--tn-highlight'] = highlight;
    const rgb = hexToRgbTriplet(highlight);
    if (rgb) navStyle['--tn-highlight-rgb'] = rgb;
  }

  // One bar entry: a menu (a tree that opens) or a link (an address).
  // `measuring` draws an inert twin for the measuring row.
  const renderEntry = (entry, measuring = false) => (entry.kind === 'menu' ? (
    <TopNavMenu
      key={entry.id}
      id={entry.id}
      label={entry.label}
      item={entry.item}
      iconFallback={entry.iconFallback}
      items={entry.items}
      emptyText={entry.emptyText}
      isOpen={!measuring && openMenuId === entry.id}
      onToggle={measuring ? () => {} : () => setOpenMenuId(openMenuId === entry.id ? null : entry.id)}
      onHoverSwitch={measuring ? () => {} : () => hoverSwitch(entry.id)}
      onNavigate={navigateTo}
    />
  ) : (
    <TopNavLink
      key={entry.id}
      label={entry.label}
      item={entry.item}
      iconFallback={entry.iconFallback}
      external={entry.external}
      onActivate={measuring ? () => {} : () => { if (entry.url) navigateTo(entry.url, entry.tabLabel); }}
      onAuxActivate={measuring ? () => {} : (e) => linkAuxActivate(e, { url: entry.url, label: entry.tabLabel })}
    />
  ));

  return (
    <header className="top-nav" ref={rootRef} style={navStyle}>
      {/* Phone width only (TopNav.css): the menubar below gives way to this. */}
      <button
        type="button"
        className="tn-drawer-btn"
        aria-label="Open navigation"
        aria-haspopup="dialog"
        aria-expanded={drawerOpen}
        onClick={() => { setOpenMenuId(null); setDrawerOpen(true); }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" />
        </svg>
      </button>
      {/* The logo — and, where the client has Workspaces switched on and
          this person can see more than one, the switch between them.
          Otherwise it is the same logo that always went Home. Its width
          is part of the More fold's arithmetic (brandRef), and it changes
          with the workspace title, so the bar watches it. */}
      <WorkspaceBrand
        ref={brandRef}
        brandName={BRAND_NAME}
        lookAndFeel={lookAndFeel}
        onHome={openHome}
        logoLight={zebraLogoOnLight}
        logoDark={zebraLogoOnDark}
      />

      <nav className="tn-menus" aria-label="Main navigation" ref={menusRef}>
        {shown.map((entry) => renderEntry(entry))}
        {folded.length > 0 && (
          <TopNavMenu
            id={MORE_ID}
            className="tn-more"
            label="More"
            item={{}}
            iconFallback="⋯"
            items={entriesAsMenuRows(folded)}
            isOpen={openMenuId === MORE_ID}
            onToggle={() => setOpenMenuId(openMenuId === MORE_ID ? null : MORE_ID)}
            onHoverSwitch={() => hoverSwitch(MORE_ID)}
            onNavigate={navigateTo}
          />
        )}
        {/* The measuring row: every entry plus the More trigger at natural
            width, never seen or reachable (see recount above). */}
        <div className="tn-measure" aria-hidden="true" inert="">
          <div className="tn-measure-row" ref={measureRef}>
            {entries.map((entry) => renderEntry(entry, true))}
            <TopNavMenu id="__more-measure" className="tn-more" label="More" item={{}} iconFallback="⋯" items={[]} isOpen={false} onToggle={() => {}} onHoverSwitch={() => {}} onNavigate={() => {}} />
          </div>
        </div>
      </nav>

      <div className="tn-search" ref={searchRef}>
        <ZebraSearchBar />
      </div>

      <div className="tn-right" ref={rightRef}>
        <NotificationBell />
        <UserAccountMenu />
      </div>

      <NavDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        entries={entries}
        onNavigate={navigateTo}
        title={lookAndFeel.titleText || BRAND_NAME}
        renderIcon={(item, fallback) => <NavItemIcon item={item} fallback={fallback} />}
        ExternalMark={ExternalMark}
      />
    </header>
  );
}
